import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useSplitStore } from '@/stores/split'
import { useOrgStore } from '@/stores/org'
import { useLayersStore } from '@/stores/layers'

// Default palette + icon for new maps and as fallbacks.
export const DEFAULT_MAP_COLOR = '#2563EB'
export const DEFAULT_MAP_ICON = 'Map'
// Default basemap key (matches DEFAULT_BASEMAP_KEY in components/maps/basemaps.ts).
export const DEFAULT_MAP_BASEMAP = 'outdoors'

// A layer is only ever rendered through a map, so one that no map claims would be
// invisible. These layers are collected into a frontend-only map instead — it has no
// row in the database and is never sent to the API.
export const VIRTUAL_MAP_ID = '__unassigned__'
const VIRTUAL_MAP_NAME = 'Unassigned'
const VIRTUAL_MAP_COLOR = '#64748b'

type OrgId = number | string

// `basemap` is a key into the BASEMAPS catalog (see components/maps/basemaps.ts).
export interface MapItem {
  id: string // uuid
  map_id?: number
  name: string
  isDefault: boolean
  isVirtual?: boolean
  color: string
  icon: string
  basemap: string
  visible: boolean
  shared: boolean
  layerCodes: string[]
  members_count?: number
}

// Normalize a server map (snake_case) to the client shape the store/getters use.
const toClientMap = (m: any): MapItem => ({
  id: m.uuid,
  map_id: m.map_id,
  name: m.name,
  isDefault: m.is_default,
  color: m.color,
  icon: m.icon,
  basemap: m.basemap,
  shared: m.shared,
  visible: true,
  layerCodes: m.layer_codes || [],
  members_count: m.members_count
})

const mapBase = (orgId: OrgId | null) => `${HOST}/org/${orgId}/map`
const orgIdOf = (explicit?: OrgId | null) => explicit ?? useOrgStore().selectedOrg

// Persist a single map's layer ordering/membership.
const persistLayers = (orgId: OrgId, map: MapItem) =>
  API({ method: 'PATCH', url: `${mapBase(orgId)}/${map.id}`, data: { layer_codes: map.layerCodes } })

interface MapsState {
  // Maps keyed by orgId, fetched from the backend.
  maps: Record<string, MapItem[]>
  // Per-map member rosters, keyed by mapId (the map uuid).
  // MapMember = { map_member_id, map_id, member_id, role_name, email,
  //   display_name, first_name, last_name, avatar_url, verified, create_dttm }
  members: Record<string, any[]>
  // Per-map pending invites, keyed by mapId.
  // MapInvite = { id, uuid, fullname, first_name, last_name, email, phone,
  //   map_id, map_role, inviter_id, is_accepted, created_at, email_status }
  invites: Record<string, any[]>
}

export const useMapsStore = defineStore('maps', {
  state: (): MapsState => ({
    maps: {},
    members: {},
    invites: {},
  }),

  getters: {
    getMaps: state => (orgId: OrgId): MapItem[] => {
      return state.maps[orgId] || []
    },

    // Top-level org layers that no persisted map claims — the safety net for any
    // backend path that fails to write a map_layer row.
    getUnassignedLayerCodes: state => (orgId: OrgId): string[] => {
      // Layers are fetched before maps, so an absent entry means "not loaded yet" —
      // treating that as "nothing claims them" would flash every layer as unassigned.
      const maps = state.maps[orgId]
      if (!maps) return []
      const claimed = new Set(maps.flatMap(m => m.layerCodes))
      return ((useLayersStore().getOrgLayers(orgId) || []) as any[])
        .filter(l => l.properties?.type !== 0 && !claimed.has(l.uuid))
        .map(l => l.uuid)
    },

    getVirtualMap() {
      return (orgId: OrgId): MapItem | null => {
        const layerCodes = this.getUnassignedLayerCodes(orgId)
        if (!layerCodes.length) return null
        return {
          id: VIRTUAL_MAP_ID,
          name: VIRTUAL_MAP_NAME,
          isVirtual: true,
          isDefault: false,
          color: VIRTUAL_MAP_COLOR,
          icon: 'Map',
          basemap: this.getDefaultMap(orgId)?.basemap || DEFAULT_MAP_BASEMAP,
          shared: false,
          visible: true,
          layerCodes
        }
      }
    },

    // Everything the UI can render. `getMaps` stays the persisted-only list: URL state
    // and the move-to-map menus must never offer the virtual map as a target.
    getAllMaps() {
      return (orgId: OrgId): MapItem[] => {
        const virtual = this.getVirtualMap(orgId)
        const maps = this.getMaps(orgId)
        return virtual ? [...maps, virtual] : maps
      }
    },

    getMapById() {
      return (orgId: OrgId, mapId: string | null | undefined): MapItem | undefined => {
        return this.getAllMaps(orgId).find(m => m.id === mapId)
      }
    },

    getDefaultMap: state => (orgId: OrgId): MapItem | undefined => {
      return (state.maps[orgId] || []).find(m => m.isDefault)
    },

    // Resolve a map's layerCodes to live layer objects from the layers store,
    // preserving the map's own ordering. Layers not yet fetched are skipped.
    getMapLayers() {
      return (orgId: OrgId, mapId: string): any[] => {
        const map = this.getAllMaps(orgId).find(m => m.id === mapId)
        if (!map) return []
        const orgLayers: any[] = useLayersStore().getOrgLayers(orgId) || []
        const byCode = new Map(orgLayers.map(l => [l.uuid, l]))
        // PlanetScope (type 0) is now a basemap, not a layer — never list it as a row.
        return map.layerCodes
          .map(code => byCode.get(code))
          .filter(Boolean)
          .filter(l => l.properties?.type !== 0)
      }
    },

    getMapForLayer() {
      return (orgId: OrgId, layerCode: string): MapItem | undefined => {
        return this.getAllMaps(orgId).find(m => m.layerCodes.includes(layerCode))
      }
    },

    getMapMembers: state => (mapId: string): any[] => {
      return state.members[mapId] || []
    },

    getMapInvites: state => (mapId: string): any[] => {
      return state.invites[mapId] || []
    },
  },

  actions: {
    setOrgMaps(orgId: OrgId, maps: MapItem[]) {
      this.maps = { ...this.maps, [orgId]: maps }
    },

    updateMap({ orgId, mapId, patch }: { orgId: OrgId, mapId: string, patch: Partial<MapItem> }) {
      this.setOrgMaps(orgId, this.getMaps(orgId).map(m => m.id === mapId ? { ...m, ...patch } : m))
    },

    moveLayerLocal(orgId: OrgId, layerCode: string, mapId: string) {
      this.setOrgMaps(orgId, this.getMaps(orgId).map(m => {
        if (m.id === mapId) {
          if (m.layerCodes.includes(layerCode)) return m
          return { ...m, layerCodes: [...m.layerCodes, layerCode] }
        }
        return { ...m, layerCodes: m.layerCodes.filter(c => c !== layerCode) }
      }))
    },

    // PATCH a map and store the normalized result.
    async patchMap(orgId: OrgId, mapId: string, data: Record<string, unknown>) {
      const { data: res } = await API({ method: 'PATCH', url: `${mapBase(orgId)}/${mapId}`, data })
      const map = toClientMap(res.map)
      this.updateMap({ orgId, mapId, patch: map })
      return map
    },

    // --- Maps CRUD ---------------------------------------------------------------

    async fetchMaps({ orgId }: { orgId: OrgId }) {
      const { data } = await API({ method: 'GET', url: mapBase(orgId) })
      const maps: MapItem[] = data.map(toClientMap)
      this.setOrgMaps(orgId, maps)
      return maps
    },

    async createMap({ orgId, name, color, icon, basemap, shared }: { orgId: OrgId, name: string, color?: string, icon?: string, basemap?: string, shared?: boolean }) {
      try {
        const { data } = await API({
          method: 'POST',
          url: `${mapBase(orgId)}/create`,
          data: { name, color, icon, basemap, shared, layer_codes: [] }
        })
        const map = toClientMap(data.map)
        this.setOrgMaps(orgId, [...this.getMaps(orgId), map])
        return map
      } catch (error) {
        toast({ title: 'Could not create map', variant: 'destructive' })
        console.error(error)
      }
    },

    renameMap({ orgId, mapId, name }: { orgId: OrgId, mapId: string, name: string }) {
      return this.patchMap(orgId, mapId, { name }).catch(err => {
        toast({ title: 'Could not rename map', variant: 'destructive' }); console.error(err)
      })
    },

    setMapStyle({ orgId, mapId, color, icon, basemap, shared }: { orgId: OrgId, mapId: string, color?: string, icon?: string, basemap?: string, shared?: boolean }) {
      const data: Record<string, unknown> = {}
      if (color !== undefined) data.color = color
      if (icon !== undefined) data.icon = icon
      if (basemap !== undefined) data.basemap = basemap
      if (shared !== undefined) data.shared = shared
      return this.patchMap(orgId, mapId, data).catch(err => {
        toast({ title: 'Could not update map', variant: 'destructive' }); console.error(err)
      })
    },

    async deleteMap({ orgId, mapId }: { orgId: OrgId, mapId: string }) {
      const target = this.getMapById(orgId, mapId)
      if (!target || target.isDefault) return
      try {
        // Drop it from the canvas first so no tile references the removed map.
        useSplitStore().hideMap({ orgId, mapId })
        await API({ method: 'DELETE', url: `${mapBase(orgId)}/${mapId}` })
        const maps = this.getMaps(orgId)
        const stored = maps.find(m => m.id === mapId)
        if (stored && !stored.isDefault) {
          const fallbackId = maps.find(m => m.isDefault)?.id
          this.setOrgMaps(orgId, maps
            .filter(m => m.id !== mapId)
            .map(m => m.id === fallbackId
              ? { ...m, layerCodes: [...m.layerCodes, ...stored.layerCodes] }
              : m))
        }
        // The deleted map's layers fall back onto the Default Map — persist that.
        const fallback = this.getDefaultMap(orgId)
        if (fallback) await persistLayers(orgId, fallback)
      } catch (error) {
        toast({ title: 'Could not delete map', variant: 'destructive' })
        console.error(error)
      }
    },

    // --- Layer ↔ map assignment --------------------------------------------------
    // A layer lives on exactly one map. The move endpoint reassigns it atomically:
    // it removes the layer from every other map in the org and attaches it here.
    async moveLayerToMap({ orgId, layerCode, mapId }: { orgId: OrgId, layerCode: string, mapId: string }) {
      // The Unassigned map is derived, not stored — layers leave it by being claimed by a
      // real map, and nothing can be moved into it.
      if (mapId === VIRTUAL_MAP_ID) return
      const originMap = this.getMapForLayer(orgId, layerCode)
      if (originMap?.id === mapId) return
      this.moveLayerLocal(orgId, layerCode, mapId)
      try {
        const target = this.getMapById(orgId, mapId)
        if (target) {
          await API({ method: 'PUT', url: `${mapBase(orgId)}/${target.id}/layer/${layerCode}` })
        }
      } catch (error) {
        // Roll the optimistic move back to the original owner so the UI doesn't show
        // the layer on the target map while the backend still has it on the source.
        // A virtual origin has no state entry — dropping the code restores it there.
        if (originMap && !originMap.isVirtual) {
          this.moveLayerLocal(orgId, layerCode, originMap.id)
        } else {
          // Drop the layer from every map — it falls back into the derived Unassigned map.
          this.setOrgMaps(orgId, this.getMaps(orgId).map(m => ({
            ...m,
            layerCodes: m.layerCodes.filter(c => c !== layerCode)
          })))
        }
        toast({ title: 'Could not move layer', variant: 'destructive' })
        console.error(error)
      }
    },

    async reorderMapLayers({ orgId, mapId, layerCodes }: { orgId: OrgId, mapId: string, layerCodes: string[] }) {
      if (mapId === VIRTUAL_MAP_ID) return
      this.setOrgMaps(orgId, this.getMaps(orgId).map(m => m.id === mapId ? { ...m, layerCodes: [...layerCodes] } : m))
      try {
        const map = this.getMapById(orgId, mapId)
        if (map) await persistLayers(orgId, map)
      } catch (error) {
        toast({ title: 'Could not reorder layers', variant: 'destructive' })
        console.error(error)
      }
    },

    // --- Map members & invites ---------------------------------------------------

    async fetchMapMembers({ orgId, mapId }: { orgId: OrgId, mapId: string }) {
      const { data } = await API({ method: 'GET', url: `${mapBase(orgId)}/${mapId}/members` })
      this.members = { ...this.members, [mapId]: data }
      return this.getMapMembers(mapId)
    },

    async fetchMapInvites({ orgId, mapId }: { orgId: OrgId, mapId: string }) {
      const { data } = await API({ method: 'GET', url: `${mapBase(orgId)}/${mapId}/invites` })
      this.invites = { ...this.invites, [mapId]: data }
      return this.getMapInvites(mapId)
    },

    async addMapInvite(payload: { orgId?: OrgId | null, map_id: string } & Record<string, any>) {
      const orgId = orgIdOf(payload.orgId)
      const mapId = payload.map_id
      try {
        const { data } = await API({
          method: 'POST',
          url: `${mapBase(orgId)}/${mapId}/invite`,
          data: {
            role_name: payload.role_name,
            email: payload.email,
            phone: payload.phone,
            first_name: payload.first_name,
            last_name: payload.last_name,
            fullname: payload.fullname
          }
        })
        this.invites = { ...this.invites, [mapId]: [...(this.invites[mapId] || []), data] }
        return data
      } catch (error) {
        toast({ title: 'Could not send invite', variant: 'destructive' })
        console.error(error)
        return null
      }
    },

    async updateMapMember({ orgId, mapId, mapMemberId, role_name }: { orgId: OrgId, mapId: string, mapMemberId: number, role_name: string }) {
      try {
        await API({ method: 'PATCH', url: `${mapBase(orgId)}/${mapId}/member/${mapMemberId}`, data: { role_name } })
        const members = this.members[mapId] || []
        this.members = {
          ...this.members,
          [mapId]: members.map(m => m.map_member_id === mapMemberId ? { ...m, role_name } : m)
        }
        toast({ title: 'Member updated' })
      } catch (error) {
        toast({ title: 'Could not update member', variant: 'destructive' })
        console.error(error)
      }
    },

    async deleteMapMember({ orgId, mapId, mapMemberId }: { orgId: OrgId, mapId: string, mapMemberId: number }) {
      try {
        await API({ method: 'DELETE', url: `${mapBase(orgId)}/${mapId}/member/${mapMemberId}` })
        this.members = {
          ...this.members,
          [mapId]: (this.members[mapId] || []).filter(m => m.map_member_id !== mapMemberId)
        }
        toast({ title: 'Member removed' })
      } catch (error) {
        toast({ title: 'Could not remove member', variant: 'destructive' })
        console.error(error)
      }
    },

    async resendMapInvite({ orgId, mapId, uuid }: { orgId?: OrgId | null, mapId: string, uuid: string }) {
      const resolvedOrg = orgIdOf(orgId)
      try {
        await API({ method: 'GET', url: `${mapBase(resolvedOrg)}/${mapId}/invite/${uuid}/resend` })
        toast({ title: 'Invite resent' })
      } catch (error) {
        toast({ title: 'Could not resend invite', variant: 'destructive' })
        console.error(error)
      }
    },

    async deleteMapInvite({ orgId, mapId, uuid }: { orgId?: OrgId | null, mapId: string, uuid: string }) {
      const resolvedOrg = orgIdOf(orgId)
      try {
        await API({ method: 'DELETE', url: `${mapBase(resolvedOrg)}/${mapId}/invite/${uuid}` })
        this.invites = {
          ...this.invites,
          [mapId]: (this.invites[mapId] || []).filter(i => i.uuid !== uuid)
        }
        toast({ title: 'Invite removed' })
      } catch (error) {
        toast({ title: 'Could not remove invite', variant: 'destructive' })
        console.error(error)
      }
    },
  },
})
